import type {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query'
import type { TokenResponse } from '@/types/api/auth'
import { logout } from '@/store/slices/authSlice'
import { AUTH_PATHS, isLoginEndpoint, isRefreshEndpoint } from './authPaths'
import { rawBaseQuery } from './baseQuery'
import { clearAccessToken, writeAccessToken } from '@/utils/tokenStorage'

type ApiBaseQuery = BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
>

let refreshPromise: Promise<boolean> | null = null

function getRequestUrl(args: string | FetchArgs): string {
  if (typeof args === 'string') {
    return args
  }
  return args.url
}

async function refreshSession(
  baseQuery: ApiBaseQuery,
  api: Parameters<ApiBaseQuery>[1],
  extraOptions: Parameters<ApiBaseQuery>[2],
): Promise<boolean> {
  const result = await baseQuery(
    {
      url: AUTH_PATHS.refresh,
      method: 'POST',
    },
    api,
    extraOptions,
  )

  if (result.error || !result.data) {
    return false
  }

  writeAccessToken((result.data as TokenResponse).accessToken)
  return true
}

export const baseQueryWithReauth: ApiBaseQuery = async (args, api, extraOptions) => {
  let result = await rawBaseQuery(args, api, extraOptions)
  const requestUrl = getRequestUrl(args)

  if (
    result.error?.status !== 401 ||
    isRefreshEndpoint(requestUrl) ||
    isLoginEndpoint(requestUrl)
  ) {
    return result
  }

  if (!refreshPromise) {
    refreshPromise = refreshSession(rawBaseQuery, api, extraOptions).finally(() => {
      refreshPromise = null
    })
  }

  const refreshed = await refreshPromise

  if (!refreshed) {
    clearAccessToken()
    api.dispatch(logout())
    return result
  }

  result = await rawBaseQuery(args, api, extraOptions)
  return result
}
